import React from 'react';
import { UncontrolledDropdown } from 'reactstrap';
import {
  Text,
  WarningOctagonIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  StyledDropdownMenu,
  StyledDropdownItem,
  VerticalRoundedDotsIcon,
  PencilIcon,
  TrashIcon,
} from '@aag-development/becreatives-ui';
import {
  RevisionCursorNavLink,
  RevisionFallbackImage,
  RevisionFallbackWrapper,
  RevisionVersionControlButton,
  RevisionMainContentCard,
  RevisionVideoControlRow,
  RevisionActionsDropdownToggle,
} from './RevisionsContentMain.styled';
import { If } from 'src/components/If';
import { RevisionVersionTooltipPopover } from './RevisionVersionTooltipPopover';
import { useTranslation } from 'react-i18next';
import { ShareMediaButton } from 'src/components/Button';
import { MediaViewer } from 'src/components/MediaCard';
import { getPreferredSource, makeSourceOptions, useMediaDownload } from 'src/features/media';
import RevisionFileDisplayFallback from 'src/assets/images/revision-file-display-fallback.png';
import { RevisionMediaList } from 'src/features/revisions';
import { BxChevronDown } from 'src/components/Boxicons';
import { cn } from 'src/lib/utils';
import { Star } from 'lucide-react';
import { RenderForRole } from 'src/components/Middleware';
import { roles } from 'src/api/services/RoleClient';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { CardErrorFallback } from 'src/features/fallback';
import { Skeleton } from 'src/components/ui/skeleton';
import { MediaSignedUrlsQueryProvider } from 'src/features/media/use-media-signed-urls-query-context';
import { RevisionVideoPlayer } from 'src/features/revisions/revision-video-player';
import { AspectRatio } from 'src/components/ui/aspect-ratio';
import { useRequestRevisionsStore } from 'src/features/revisions/use-revisions-context-provider';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { RequestCommentsQueryContextProvider } from 'src/features/comments/use-request-comments';
import { EmptySpaceFallbackImage } from 'src/features/fallback/ui';
import {
  VideoPlayerContextProvider,
  VideoPlayerSkeleton,
} from 'src/features/video-player/video-player';
import {
  RevisionChatContent,
  RevisionChatSection,
} from '../../revisions-content-main/revision-chat-content';

const maxRating = 5;

const RevisionStars: React.FC<
  React.ComponentProps<'div'> & {
    rating?: number;
  }
> = ({ className, rating, ...props }) => {
  if (!rating) {
    return null;
  }

  return (
    <RenderForRole roles={[roles.teamLead, roles.admin]}>
      <div className={cn('tw-flex tw-gap-2', className)} {...props}>
        {Array.from({ length: maxRating }, (_, i) => (
          <Star
            key={i}
            className={cn('tw-size-5 tw-stroke-1', {
              'tw-fill-purple-300 tw-stroke-purple-500': i + 1 <= rating,
            })}
          />
        ))}
      </div>
    </RenderForRole>
  );
};

const RevisionsContentMain: React.FC = () => {
  const { t } = useTranslation('pages/shared');

  const request = useDecoratedRequestContext();
  const {
    selectedRevision,
    selectedRevisionMedia,
    revisions,
    deleteRevisionMutation,
    select,
    selectPrevious,
    selectNext,
    canSelectNext,
    canSelectPrevious,
  } = useRequestRevisionsStore();
  const { downloadAll, isDownloading } = useMediaDownload();

  return (
    <div className={'tw-mb-4 tw-grid tw-gap-4 lg:tw-grid-cols-12'}>
      <RevisionMainContentCard
        color={'white'}
        className={'tw-mb-0 tw-self-start lg:tw-col-span-7 xl:tw-col-span-8'}
      >
        {/*Content top*/}
        <RevisionVideoControlRow>
          <UncontrolledDropdown>
            <RevisionActionsDropdownToggle tag={'span'}>
              <VerticalRoundedDotsIcon color={'dark'} />
            </RevisionActionsDropdownToggle>

            <StyledDropdownMenu>
              <StyledDropdownItem disabled={true}>
                <PencilIcon className={'me-2'} color={'dark'} />
                {t('revisions.revision.action.edit')}
              </StyledDropdownItem>
              <StyledDropdownItem
                onClick={() => {
                  deleteRevisionMutation.mutate(selectedRevision?.raw?.id ?? '');
                }}
                disabled={
                  deleteRevisionMutation.isLoading ||
                  !selectedRevision ||
                  !selectedRevision.canDelete
                }
              >
                <TrashIcon className={'me-2'} color={'primary'} />
                {t('revisions.revision.action.delete')}
              </StyledDropdownItem>
            </StyledDropdownMenu>
          </UncontrolledDropdown>

          <UncontrolledDropdown className={'me-auto'}>
            <RevisionVersionControlButton tag={'span'}>
              {selectedRevision?.title || t('unset', { ns: 'common' })}
              <BxChevronDown className={'ms-2'} />
            </RevisionVersionControlButton>
            <StyledDropdownMenu>
              {/* Create a copy & reverse to sort by desc */}
              {[...revisions].reverse().map((revision, i) => (
                <StyledDropdownItem
                  key={i}
                  className={cn({
                    'active text-primary': revision.title === selectedRevision?.title,
                  })}
                  onClick={() => {
                    if (selectedRevision?.raw.id !== revision.raw.id) {
                      select(revision);
                    }
                  }}
                >
                  {revision.title}
                </StyledDropdownItem>
              ))}
            </StyledDropdownMenu>
          </UncontrolledDropdown>

          <UncontrolledDropdown>
            <RevisionVersionControlButton className={'ms-auto me-4'} tag={'span'}>
              <i className={'bx bx-cloud-download fs-4 me-2'} />
              Download files
            </RevisionVersionControlButton>
            <StyledDropdownMenu>
              <StyledDropdownItem
                onClick={() => {
                  downloadAll([selectedRevisionMedia!]);
                }}
                disabled={!selectedRevisionMedia || isDownloading}
              >
                <i className={'bx bx-cloud-download fs-3 text-info me-2'} />{' '}
                {selectedRevisionMedia?.file_name ?? '-'}
              </StyledDropdownItem>
              <StyledDropdownItem
                onClick={() => {
                  // Simplify using a single mapping instead of filtering multiple times with different collection_name values
                  const allCollectionMedia = selectedRevision?.media
                    .map((m) => ({ ...m, collection_name: m.collection_name }))
                    .filter((m) => ['default', 'resizes', 'thumbnails', 'captions', 'project_files', 'transcript'].includes(m.collection_name));
                  downloadAll(allCollectionMedia ?? []);
                }}
                disabled={!selectedRevision?.hasMedia}
              >
                <i className={'bx bx-cloud-download fs-3 text-info me-2'} /> All current version
                files
              </StyledDropdownItem>
            </StyledDropdownMenu>
          </UncontrolledDropdown>

          <If when={!!selectedRevisionMedia}>
            <ShareMediaButton
              mediaId={selectedRevisionMedia?.id ?? ''}
              requestId={request.id}
              showAiDescription={true}
              className={'fs-5 me-4 pointer text-primary'}
              title={'Share'}
            />
          </If>

          <RevisionVersionTooltipPopover placement={'left'} />
        </RevisionVideoControlRow>

        {/*Content main*/}
        <If
          when={!selectedRevision?.isFirstContact}
          else={
            <RevisionFallbackWrapper>
              <RevisionFallbackImage src={RevisionFileDisplayFallback} />
              <Text color={'gray'} $size={'md'} $md={'xl'} $centeredContent={true}>
                <WarningOctagonIcon className={'me-2'} />
                Create a video revision to add files
              </Text>
            </RevisionFallbackWrapper>
          }
        >
          {/* Revision is not first contact, render video player with selected revision media list */}
          <div className={'tw-flex tw-w-full tw-flex-col tw-gap-2 xl:tw-flex-row'}>
            <RevisionMediaList />

            <If
              when={selectedRevision?.hasMedia}
              else={
                <AspectRatio
                  ratio={16 / 9}
                  className={
                    'tw-flex tw-w-full tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-self-start'
                  }
                >
                  <RevisionFallbackImage src={RevisionFileDisplayFallback} />
                  <Text color={'gray'} $size={'md'} $md={'xl'} $centeredContent={true}>
                    <WarningOctagonIcon className={'me-2'} />
                    This revision has no files
                  </Text>
                </AspectRatio>
              }
            >
              {/*If media can't be played, render generic media viewed*/}
              <If
                when={selectedRevisionMedia?.canPlay}
                else={
                  <AspectRatio ratio={16 / 9}>
                    {!!selectedRevisionMedia && (
                      <MediaViewer
                        renderOriginal={true}
                        media={selectedRevisionMedia}
                        className={
                          'tw-mx-auto !tw-h-full !tw-w-auto !tw-rounded-md !tw-object-contain'
                        }
                      />
                    )}
                  </AspectRatio>
                }
              >
                <div className={'tw-grow'}>
                  <QueryErrorResetBoundary>
                    {({ reset }) => (
                      <ErrorBoundary
                        fallbackRender={({ error, resetErrorBoundary }: any) => (
                          <CardErrorFallback
                            error={error}
                            resetErrorBoundary={resetErrorBoundary}
                          />
                        )}
                        onReset={reset}
                      >
                        <React.Suspense fallback={<VideoPlayerSkeleton />}>
                          <MediaSignedUrlsQueryProvider id={selectedRevisionMedia!.id}>
                            <VideoPlayerContextProvider
                              meta={{
                                fps: selectedRevisionMedia?.fps,
                                durationInSeconds: selectedRevisionMedia?.durationInSeconds,
                              }}
                              defaultSrc={getPreferredSource(selectedRevisionMedia!)}
                              srcOptions={makeSourceOptions(selectedRevisionMedia!)}
                            >
                              <RevisionVideoPlayer />
                            </VideoPlayerContextProvider>
                          </MediaSignedUrlsQueryProvider>
                        </React.Suspense>
                      </ErrorBoundary>
                    )}
                  </QueryErrorResetBoundary>
                </div>
              </If>
            </If>
          </div>
        </If>

        {/*Content bottom*/}
        <RevisionVideoControlRow className={'my-2'}>
          <RevisionCursorNavLink
            color={'primary'}
            disabled={!canSelectPrevious}
            onClick={selectPrevious}
          >
            <ChevronLeftIcon color={'primary'} className={'me-2'} />
            {t('revisions.revision.action.prev')}
          </RevisionCursorNavLink>

          <RevisionStars
            className={'tw-hidden sm:tw-flex'}
            rating={selectedRevision?.raw?.rating}
          />

          <RevisionCursorNavLink color={'primary'} disabled={!canSelectNext} onClick={selectNext}>
            {t('revisions.revision.action.next')}
            <ChevronRightIcon color={'primary'} className={'ms-2'} />
          </RevisionCursorNavLink>
        </RevisionVideoControlRow>

        <RevisionStars className={'sm:tw-hidden'} rating={selectedRevision?.raw?.rating} />
      </RevisionMainContentCard>

      {/* Request revision chat */}
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }: any) => (
              <RevisionChatSection className={'lg:tw-col-span-5 xl:tw-col-span-4'}>
                <EmptySpaceFallbackImage className={'tw-mt-auto tw-self-center'} />
                <CardErrorFallback
                  className={'tw-mt-auto tw-w-full tw-border-none tw-shadow-none'}
                  error={error}
                  resetErrorBoundary={resetErrorBoundary}
                  description={t('alerts:error.failed_to_load_comments', {
                    defaultValue: 'Failed to load comments',
                  })}
                />
              </RevisionChatSection>
            )}
            onReset={reset}
          >
            <React.Suspense
              fallback={
                <Skeleton className={'tw-h-44 lg:tw-col-span-5 lg:tw-h-auto xl:tw-col-span-4'} />
              }
            >
              <RequestCommentsQueryContextProvider
                request={request.id}
                filters={{
                  limit: 50,
                  from_revision: selectedRevision?.id,
                }}
              >
                <RevisionChatSection className={'lg:tw-col-span-5 xl:tw-col-span-4'}>
                  <RevisionChatContent />
                </RevisionChatSection>
              </RequestCommentsQueryContextProvider>
            </React.Suspense>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </div>
  );
};

export { RevisionsContentMain };
